import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UNITS } from "@config";
import { useGraphColors } from "@core";

import { BaseTimeSeriesChart, TimeSeriesData, YAxisConfig } from "../charts/BaseTimeSeriesChart";
import type { DateRange, GraphSettings, Resolution, TypedChartSeries } from "../shared/types";

export enum HeatPowerSeriesType {
  MEASURED_HEAT_POWER = "measured_heat_power",
  EQUIVALENT_POWER = "equivalent_power",
}

type HeatPowerChartContainerProps = {
  data: TypedChartSeries<HeatPowerSeriesType>[];
  isLoading?: boolean;
  chartHeight?: number;
  dateRange?: DateRange;
  resolution?: Resolution;
  graphSettings?: GraphSettings;
};

export const HeatPowerChartContainer: React.FC<HeatPowerChartContainerProps> = ({
  data,
  isLoading = false,
  chartHeight,
  dateRange,
  resolution,
  graphSettings,
}) => {
  const { t } = useTranslation(["propai_properties"]);
  const graphColors = useGraphColors();

  const HEAT_POWER_SERIES_CONFIG = useMemo(
    (): Record<
      HeatPowerSeriesType,
      {
        name: string;
        unit: string;
        color: string;
        yAxisIndex: number;
        visible: boolean;
      }
    > => ({
      [HeatPowerSeriesType.MEASURED_HEAT_POWER]: {
        name: t("text_heat_power_measured"),
        unit: ` ${UNITS.heat}`,
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      },
      [HeatPowerSeriesType.EQUIVALENT_POWER]: {
        name: t("text_change_in_heat_power"),
        unit: ` ${UNITS.heat}`,
        color: graphColors[1],
        yAxisIndex: 0,
        visible: true,
      },
    }),
    [t, graphColors]
  );

  const Y_AXIS_CONFIGS = useMemo(
    (): YAxisConfig[] => [
      {
        title: { text: `Heat Power (${UNITS.heat})` },
        lineWidth: 1,
        gridLineWidth: 0,
      },
    ],
    []
  );

  const series = useMemo((): TimeSeriesData[] => {
    const result: TimeSeriesData[] = [];

    data.forEach((seriesData) => {
      const config = HEAT_POWER_SERIES_CONFIG[seriesData.type];
      if (config && seriesData.data.length > 0) {
        result.push({
          uid: seriesData.type,
          name: config.name,
          data: seriesData.data,
          unit: config.unit,
          color: config.color,
          yAxisIndex: config.yAxisIndex,
          visible: config.visible,
        });
      }
    });

    return result;
  }, [data, HEAT_POWER_SERIES_CONFIG]);

  return (
    <BaseTimeSeriesChart
      series={series}
      yAxisConfigs={Y_AXIS_CONFIGS}
      chartHeight={chartHeight}
      dateRange={dateRange}
      resolution={resolution}
      graphSettings={graphSettings}
      isLoading={isLoading}
      allowSeriesToggle={true}
      yAxisLayout="side-by-side"
    />
  );
};
